import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { chambersApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, Briefcase, Crown, EnvelopeSimple, Pencil, SignOut, TrashSimple, UserPlus, Users } from "@phosphor-icons/react";
import { ChamberForm } from "./Chambers";
import { toast } from "sonner";

const ROLE_DESCRIPTIONS = {
    owner: "Full control. Can delete the chamber and transfer ownership.",
    admin: "Manage members. Full CRUD on chamber matters.",
    associate: "Create and edit matters, documents, drafts.",
    paralegal: "Read access + upload documents.",
};

export default function ChamberDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            setData(await chambersApi.get(id));
        } catch (e) {
            toast.error("Failed to load chamber");
            navigate("/chambers");
        }
    }, [id, navigate]);

    useEffect(() => { load(); }, [load]);

    const remove = async () => {
        if (!window.confirm("Delete this chamber? Matters will revert to personal.")) return;
        await chambersApi.remove(id);
        toast.success("Chamber deleted");
        navigate("/chambers");
    };

    const leave = async () => {
        if (!window.confirm("Leave this chamber?")) return;
        await chambersApi.leave(id);
        navigate("/chambers");
    };

    if (!data) return <div className="p-8 text-sm text-muted-foreground">Loading chamber…</div>;
    const isOwner = data.my_role === "owner";
    const canManage = isOwner || data.my_role === "admin";

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="chamber-detail">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2 -ml-2"><ArrowLeft size={14} /> Back</Button>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
                <div className="rule-line pt-3">
                    <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Chamber</div>
                    <h1 className="font-serif text-4xl tracking-tight">{data.name}</h1>
                    {data.description && <p className="text-sm text-foreground/80 mt-1 max-w-prose">{data.description}</p>}
                    <span className="chip chip-doc inline-flex items-center gap-1 mt-3">
                        {isOwner && <Crown size={11} weight="fill" />} You are: {data.my_role}
                    </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {canManage && (
                        <Dialog open={editOpen} onOpenChange={setEditOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" size="sm" data-testid="edit-chamber-btn"><Pencil size={14} /> Edit</Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle className="font-serif">Edit chamber</DialogTitle></DialogHeader>
                                <ChamberForm initial={data} onDone={() => { setEditOpen(false); load(); }} />
                            </DialogContent>
                        </Dialog>
                    )}
                    {canManage && (
                        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                            <DialogTrigger asChild>
                                <Button size="sm" data-testid="invite-member-btn"><UserPlus size={14} /> Invite</Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle className="font-serif">Invite a colleague</DialogTitle></DialogHeader>
                                <InviteForm chamberId={id} onDone={() => { setInviteOpen(false); load(); }} />
                            </DialogContent>
                        </Dialog>
                    )}
                    {isOwner ? (
                        <Button variant="outline" size="sm" onClick={remove} className="text-destructive" data-testid="delete-chamber-btn">
                            <TrashSimple size={14} /> Delete
                        </Button>
                    ) : (
                        <Button variant="outline" size="sm" onClick={leave} data-testid="leave-chamber-btn">
                            <SignOut size={14} /> Leave
                        </Button>
                    )}
                </div>
            </div>

            <Tabs defaultValue="members">
                <TabsList>
                    <TabsTrigger value="members" data-testid="tab-members"><Users size={14} className="mr-1" /> Members ({data.members.length})</TabsTrigger>
                    <TabsTrigger value="matters" data-testid="tab-cmatters"><Briefcase size={14} className="mr-1" /> Shared matters ({data.matters.length})</TabsTrigger>
                </TabsList>

                <TabsContent value="members" className="mt-4">
                    <div className="border border-border rounded-md bg-white overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted text-muted-foreground">
                                <tr>
                                    <th className="text-left p-3">Member</th>
                                    <th className="text-left p-3">Role</th>
                                    <th className="text-left p-3">Status</th>
                                    <th className="text-left p-3">Joined</th>
                                    {canManage && <th className="text-right p-3">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {data.members.map((m) => (
                                    <tr key={m.id} className="border-t border-border" data-testid={`member-row-${m.id}`}>
                                        <td className="p-3">
                                            <div className="font-medium">{m.name || m.email}</div>
                                            <div className="text-xs text-muted-foreground">{m.email}</div>
                                        </td>
                                        <td className="p-3">
                                            {isOwner && m.role !== "owner" ? (
                                                <Select value={m.role} onValueChange={async (val) => {
                                                    try { await chambersApi.changeRole(id, m.id, val); toast.success("Role updated"); load(); }
                                                    catch { toast.error("Failed"); }
                                                }}>
                                                    <SelectTrigger className="h-8 w-[130px]" data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="admin">Admin</SelectItem>
                                                        <SelectItem value="associate">Associate</SelectItem>
                                                        <SelectItem value="paralegal">Paralegal</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                <span className="chip chip-doc inline-flex items-center gap-1">
                                                    {m.role === "owner" && <Crown size={11} weight="fill" />}{m.role}
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-3">
                                            <span className={`chip ${m.status === "active" ? "chip-internal" : "chip-ai"}`}>{m.status}</span>
                                        </td>
                                        <td className="p-3 text-xs text-muted-foreground">
                                            {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : (m.invited_at ? `Invited ${new Date(m.invited_at).toLocaleDateString()}` : "—")}
                                        </td>
                                        {canManage && (
                                            <td className="p-3 text-right">
                                                {m.role !== "owner" && (
                                                    <button
                                                        className="text-muted-foreground hover:text-destructive"
                                                        onClick={async () => {
                                                            if (!window.confirm("Remove this member?")) return;
                                                            try { await chambersApi.removeMember(id, m.id); toast.success("Removed"); load(); }
                                                            catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                                                        }}
                                                        data-testid={`remove-member-${m.id}`}
                                                    ><TrashSimple size={14} /></button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4 text-xs text-muted-foreground space-y-1">
                        {Object.entries(ROLE_DESCRIPTIONS).map(([k, v]) => <div key={k}><b className="capitalize">{k}:</b> {v}</div>)}
                    </div>
                </TabsContent>

                <TabsContent value="matters" className="mt-4">
                    {!data.matters.length ? (
                        <div className="text-sm text-muted-foreground">No matters in this chamber yet.</div>
                    ) : (
                        <div className="grid gap-3 md:grid-cols-2">
                            {data.matters.map((m) => (
                                <Link key={m.id} to={`/matters/${m.id}`} className="border border-border rounded-md bg-white p-4 hover:border-primary/40">
                                    <div className="font-serif text-lg">{m.title}</div>
                                    <div className="text-xs text-muted-foreground mt-1">{m.court || "—"} · {m.case_number || "—"}</div>
                                </Link>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}

function InviteForm({ chamberId, onDone }) {
    const [v, setV] = useState({ email: "", role: "associate" });
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        if (!v.email.trim()) { toast.error("Email required"); return; }
        setSaving(true);
        try {
            const res = await chambersApi.invite(chamberId, v.email.trim(), v.role);
            toast.success(res.status === "active" ? "Member added" : "Invitation sent");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); }
        setSaving(false);
    };
    return (
        <div className="space-y-3" data-testid="invite-form">
            <div className="space-y-1">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Email</div>
                <Input type="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} placeholder="colleague@email.com" data-testid="invite-email" />
            </div>
            <div className="space-y-1">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Role</div>
                <Select value={v.role} onValueChange={(val) => setV({ ...v, role: val })}>
                    <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="associate">Associate</SelectItem>
                        <SelectItem value="paralegal">Paralegal</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <p className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[v.role]}</p>
            <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background border-t border-border flex justify-end mt-2"><Button onClick={submit} disabled={saving} data-testid="send-invite-btn"><EnvelopeSimple size={14} /> {saving ? "Sending…" : "Send invite"}</Button></div>
        </div>
    );
}
